import { useState, useEffect } from 'react';
import { SitesService } from '@/services/sitesService';
import { supabase } from '@/integrations/supabase/client';

interface SiteGoogleData {
  siteName: string;
  rating: number | null;
  reviewCount: number;
}

interface GooglePlacesSummary {
  averageRating: number;
  totalReviews: number;
  sitesWithRatings: number;
  totalSites: number;
  sitesData: SiteGoogleData[];
}

export const useGooglePlacesSummary = () => {
  const [data, setData] = useState<GooglePlacesSummary>({
    averageRating: 0,
    totalReviews: 0,
    sitesWithRatings: 0,
    totalSites: 0,
    sitesData: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllSitesData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('🔍 Fetching all sites data for summary...');
        
        // Récupérer tous les sites
        const sites = await SitesService.getAllSites();
        console.log('📍 Found sites:', sites.length);

        if (sites.length === 0) {
          setData({
            averageRating: 0,
            totalReviews: 0,
            sitesWithRatings: 0,
            totalSites: 0,
            sitesData: []
          });
          return;
        }

        // Récupérer les données Google Places pour chaque site
        const sitesPromises = sites.map(async (site) => {
          try {
            console.log('🏢 Fetching Google data for:', site.name);
            
            const { data: result, error: functionError } = await supabase.functions.invoke('google-places-details', {
              body: { 
                siteName: site.name, 
                address: site.address, 
                city: site.city 
              }
            });

            if (functionError) {
              console.error(`Error for ${site.name}:`, functionError);
              return {
                siteName: site.name,
                rating: null,
                reviewCount: 0
              };
            }

            return {
              siteName: site.name,
              rating: result?.rating || null,
              reviewCount: result?.reviewCount || 0
            };
          } catch (err) {
            console.error(`Error fetching data for ${site.name}:`, err);
            return {
              siteName: site.name,
              rating: null,
              reviewCount: 0
            };
          }
        });

        const sitesData = await Promise.all(sitesPromises);
        console.log('📊 Sites data collected:', sitesData);

        // Calculer les statistiques
        const sitesWithRatings = sitesData.filter(site => site.rating !== null);
        const totalReviews = sitesData.reduce((sum, site) => sum + site.reviewCount, 0);
        const averageRating = sitesWithRatings.length > 0
          ? sitesWithRatings.reduce((sum, site) => sum + (site.rating || 0), 0) / sitesWithRatings.length
          : 0;

        const summary: GooglePlacesSummary = {
          averageRating: Math.round(averageRating * 10) / 10, // Arrondir à 1 décimale
          totalReviews,
          sitesWithRatings: sitesWithRatings.length,
          totalSites: sites.length,
          sitesData
        };

        console.log('📈 Summary calculated:', summary);
        setData(summary);

      } catch (err) {
        console.error('Error fetching sites summary:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchAllSitesData();
  }, []);

  return { data, loading, error };
};